import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import Swal from 'sweetalert2'
import './BoostCreditFacilityDetails.scss'
import { boostCreditBXService } from 'BoostXpressAdmin/Services/boostCreditBXService'
import { BoostCreditFacility, DirectDebitAttempt, FacilityRepayment, DirectDebitBalanceData } from 'BoostXpressAdmin/Model/boostCreditXModel'
import { CurrencyFormatter, TimeConverter, handleSuccess } from 'Helpers'
import { HorizontalLoader } from 'Components'
import { handleError } from 'Helpers/alert/alerts'
import backArrow from '../../../../../Assets/Icon/Users/back-arrow-detailed.svg'
import { ToastContainer } from 'react-toastify'

const BoostCreditFacilityDetails = () => {
  const { facilityId } = useParams<{ facilityId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const facilityFromState = location.state?.facility as BoostCreditFacility | undefined

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [facility, setFacility] = useState<BoostCreditFacility | null>(facilityFromState || null)

  // Debit attempts state
  const [debitAttempts, setDebitAttempts] = useState<DirectDebitAttempt[]>([])
  const [debitAttemptsLoading, setDebitAttemptsLoading] = useState(false)
  const [debitAttemptsError, setDebitAttemptsError] = useState<string | null>(null)
  const [debitAttemptsPage, setDebitAttemptsPage] = useState(1)
  const [debitAttemptsTotal, setDebitAttemptsTotal] = useState(0)
  const [debitStatusFilter, setDebitStatusFilter] = useState('')
  const [reconcilingAttemptId, setReconcilingAttemptId] = useState<string | null>(null)

  // Repayments state
  const [repayments, setRepayments] = useState<FacilityRepayment[]>([])
  const [repaymentsLoading, setRepaymentsLoading] = useState(false)
  const [repaymentsError, setRepaymentsError] = useState<string | null>(null)
  const [repaymentsPage, setRepaymentsPage] = useState(1)
  const [repaymentsTotal, setRepaymentsTotal] = useState(0)
  const [sourceFilter, setSourceFilter] = useState('')

  // Direct debit modal state
  const [showDebitModal, setShowDebitModal] = useState(false)
  const [debitAmount, setDebitAmount] = useState('')
  const [debitSubmitting, setDebitSubmitting] = useState(false)

  // Bank balance check modal state
  const [showBalanceModal, setShowBalanceModal] = useState(false)
  const [showBalanceAmountModal, setShowBalanceAmountModal] = useState(false)
  const [balanceAmount, setBalanceAmount] = useState('')
  const [balanceLoading, setBalanceLoading] = useState(false)
  const [balanceData, setBalanceData] = useState<DirectDebitBalanceData | null>(null)
  const [balanceError, setBalanceError] = useState<string | null>(null)

  const loadDebitAttempts = async () => {
    if (!facilityId) return

    try {
      setDebitAttemptsLoading(true)
      setDebitAttemptsError(null)

      const params: any = {
        page: debitAttemptsPage,
        limit: 30,
        sortOrder: 'DESC',
      }

      if (debitStatusFilter) params.status = debitStatusFilter

      const res = await boostCreditBXService.getDirectDebitAttempts(facilityId, params)
      setDebitAttempts(Array.isArray(res.data.data) ? res.data.data : [])
      setDebitAttemptsTotal(res.data?.data?.length || 0)
    } catch (e: any) {
      setDebitAttemptsError(e.message || 'Failed to load debit attempts')
      setDebitAttempts([])
      setDebitAttemptsTotal(0)
    } finally {
      setDebitAttemptsLoading(false)
    }
  }

  const loadRepayments = async () => {
    if (!facilityId) return

    try {
      setRepaymentsLoading(true)
      setRepaymentsError(null)

      const params: any = {
        page: repaymentsPage,
        limit: 30,
        sortOrder: 'DESC',
      }

      if (sourceFilter) params.source = sourceFilter

      const res = await boostCreditBXService.getFacilityRepayments(facilityId, params)
      setRepayments(Array.isArray(res.data.data) ? res.data.data : [])
      setRepaymentsTotal(res.data?.data?.length || 0)
    } catch (e: any) {
      setRepaymentsError(e.message || 'Failed to load repayments')
      setRepayments([])
      setRepaymentsTotal(0)
    } finally {
      setRepaymentsLoading(false)
    }
  }

  const handleReconcileAttempt = async (attemptResourceId: string) => {
    const result = await Swal.fire({
      title: 'Reconcile Direct Debit Attempt',
      text: 'This will retrieve the live Mono debit status and apply any status changes locally on the Tradegrid backend. Do you want to proceed?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#E873A8',
      cancelButtonColor: '#d1d5db',
      confirmButtonText: 'Yes, Reconcile',
      cancelButtonText: 'Cancel',
    })

    if (result.isConfirmed) {
      try {
        setReconcilingAttemptId(attemptResourceId)
        await boostCreditBXService.reconcileDirectDebitAttempt(attemptResourceId)
        handleSuccess('Direct debit attempt reconciled successfully')
        loadDebitAttempts()
      } catch (e: any) {
        handleError(e)
      } finally {
        setReconcilingAttemptId(null)
      }
    }
  }

  const getDebitStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'success':
        return '#10b981'
      case 'pending':
        return '#f59e0b'
      case 'retrying':
        return '#3b82f6'
      case 'failed':
        return '#ef4444'
      default:
        return '#6b7280'
    }
  }

  const getDebitStatusBgColor = (status: string) => {
    switch (status) {
      case 'success':
        return '#d1fae5'
      case 'pending':
        return '#fef3c7'
      case 'retrying':
        return '#dbeafe'
      case 'failed':
        return '#fee2e2'
      default:
        return '#f3f4f6'
    }
  }

  const getFacilityStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#10b981'
      case 'pending_activation':
        return '#f59e0b'
      case 'paused':
        return '#3b82f6'
      case 'suspended':
        return '#ef4444'
      case 'blacklisted':
        return '#dc2626'
      default:
        return '#6b7280'
    }
  }

  const getFacilityStatusBgColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#d1fae5'
      case 'pending_activation':
        return '#fef3c7'
      case 'paused':
        return '#dbeafe'
      case 'suspended':
        return '#fee2e2'
      case 'blacklisted':
        return '#fecaca'
      default:
        return '#f3f4f6'
    }
  }

  const handleTriggerDirectDebit = async () => {
    if (!facility) return

    const result = await Swal.fire({
      title: 'Trigger Direct Debit',
      text: 'This will initiate a direct debit on the user\'s account. Do you want to proceed?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E873A8',
      cancelButtonColor: '#d1d5db',
      confirmButtonText: 'Yes, Proceed',
      cancelButtonText: 'Cancel',
    })

    if (result.isConfirmed) {
      setDebitAmount(facility.outstandingMajor.toString())
      setShowDebitModal(true)
    }
  }

  const handleCheckBankBalance = async () => {
    if (!facility) return

    const result = await Swal.fire({
      title: 'Check Bank Account Balance',
      html: 'This checks the actual bank balance and determines if the user\'s account has sufficient funds to cover the due amount.<br><br><strong>What it does:</strong><br>• Retrieves the live bank account balance<br>• Checks if sufficient for total due amount<br>• Returns account balance and sufficiency status',
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#E873A8',
      cancelButtonColor: '#d1d5db',
      confirmButtonText: 'Proceed',
      cancelButtonText: 'Cancel',
    })

    if (result.isConfirmed) {
      setBalanceAmount(facility.outstandingMajor.toString())
      setShowBalanceAmountModal(true)
    }
  }

  const loadBankBalance = async () => {
    if (!facilityId) return

    try {
      Swal.fire({
        title: 'Checking Bank Balance',
        text: 'Please wait while we check the user\'s bank account balance...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        },
      })

      setBalanceLoading(true)
      setBalanceError(null)
      const res = await boostCreditBXService.checkDirectDebitBalance(facilityId)
      setBalanceData(res.data)
      setShowBalanceModal(true)
      setShowBalanceAmountModal(false)
      Swal.close()
    } catch (e: any) {
      setBalanceError(e.message || 'Failed to check bank balance')
      handleError(e)
      Swal.close()
    } finally {
      setBalanceLoading(false)
    }
  }

  const handleDebitSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!facility || !facilityId) return

    const amount = parseFloat(debitAmount)
    if (isNaN(amount) || amount <= 0) {
      handleError({ message: 'Please enter a valid amount' })
      return
    }

    try {
      setDebitSubmitting(true)
      await boostCreditBXService.triggerDirectDebit(facilityId, amount)
      handleSuccess('Direct debit triggered successfully')
      setShowDebitModal(false)
      setDebitAmount('')
      loadDebitAttempts()
    } catch (e: any) {
      handleError(e)
    } finally {
      setDebitSubmitting(false)
    }
  }

  useEffect(() => {
    if (facility) {
      loadDebitAttempts()
      loadRepayments()
    }
  }, [facility, debitAttemptsPage, debitStatusFilter, repaymentsPage, sourceFilter])

  return (
    <div className='bx-facility-details-page'>
      <header className='bx-facility-header'>
        <div className='bx-breadcrumb-container'>
          <button className='bx-back-btn' onClick={() => navigate(-1)}>
            <img src={backArrow} alt='Back' /> 
          </button> 
          <div className='bx-breadcrumb-nav'>
            <span className='bx-breadcrumb-item'>Credit Facilities</span>
            <span className='bx-breadcrumb-separator'>/</span>
            <span className='bx-breadcrumb-active'>Facility Details</span>
          </div>
        </div>
      </header>

      {loading ? (
        <div className='bx-loading-container'>
          <HorizontalLoader />
        </div>
      ) : error ? (
        <div className='bx-error-container'>
          <div className='alert alert-danger'>{error}</div>
        </div>
      ) : !facility ? (
        <div className='bx-error-container'>
          <div className='alert alert-info'>Facility not found</div>
        </div>
      ) : (
        <div className='bx-facility-content'>
          <section className='bx-facility-overview'>
            <div className='bx-facility-header-info'>
              <div className='bx-facility-id-badge'>
                <small className='bx-label'>Facility ID</small>
                <span className='bx-id'>{facility.resourceId}</span>
              </div>
              <div className='bx-facility-header-actions'>
                <div className='bx-facility-status-badge'>
                  <span
                    className='bx-status-badge'
                    style={{
                      backgroundColor: getFacilityStatusBgColor(facility.status),
                      color: getFacilityStatusBadgeColor(facility.status),
                    }}
                  >
                    {facility.status.replace(/_/g, ' ').toUpperCase()}
                  </span>
                </div>
                <button
                  className='bx-check-balance-btn'
                  onClick={handleCheckBankBalance}
                >
                  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 6C12.55 6 13 6.45 13 7V11C13 11.55 12.55 12 12 12C11.45 12 11 11.55 11 11V7C11 6.45 11.45 6 12 6ZM12 14C12.55 14 13 14.45 13 15C13 15.55 12.55 16 12 16C11.45 16 11 15.55 11 15C11 14.45 11.45 14 12 14Z' fill='#E873A8'/>
                  </svg>
                  Check Bank Balance
                </button>
                <button
                  className='bx-trigger-debit-btn'
                  onClick={handleTriggerDirectDebit}
                >
                  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 13H11V7H13V13ZM13 17H11V15H13V17Z' fill='#ffffff'/>
                  </svg>
                  Trigger Direct Debit
                </button>
              </div>
            </div>
          </section>

          <section className='bx-facility-metrics'>
            <div className='bx-metric-card bx-metric-card--primary'>
              <div className='bx-metric-icon'>
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 17C11.4477 17 11 16.5523 11 16C11 15.4477 11.4477 15 12 15C12.5523 15 13 15.4477 13 16C13 16.5523 12.5523 17 12 17ZM13 13H11V7H13V13Z' fill='#3b82f6'/>
                </svg>
              </div>
              <div className='bx-metric-content'>
                <span className='bx-metric-label'>Gross Limit</span>
                <h3 className='bx-metric-value'>₦{CurrencyFormatter(facility.grossLimitMajor)}</h3>
              </div>
            </div>
            <div className='bx-metric-card bx-metric-card--success'>
              <div className='bx-metric-icon'>
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM17 13H13V17H11V13H7V11H13V7H17V13Z' fill='#10b981'/>
                </svg>
              </div>
              <div className='bx-metric-content'>
                <span className='bx-metric-label'>Available</span>
                <h3 className='bx-metric-value'>₦{CurrencyFormatter(facility.availableMajor)}</h3>
              </div>
            </div>
            <div className='bx-metric-card bx-metric-card--warning'>
              <div className='bx-metric-icon'>
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 17C11.4477 17 11 16.5523 11 16C11 15.4477 11.4477 15 12 15C12.5523 15 13 15.4477 13 16C13 16.5523 12.5523 17 12 17ZM13 13H11V7H13V13Z' fill='#f59e0b'/>
                </svg>
              </div>
              <div className='bx-metric-content'>
                <span className='bx-metric-label'>Outstanding</span>
                <h3 className='bx-metric-value'>₦{CurrencyFormatter(facility.outstandingMajor ?? 0)}</h3>
              </div>
            </div>
          </section>

          <section className='bx-facility-details-section'>
            <h2 className='bx-section-title'>Facility Information</h2>
            <div className='bx-info-grid'>
              <div className='bx-info-item'>
                <span className='bx-info-label'>Mandate ID</span>
                <span className='bx-info-value'>{facility.mandateId || '-'}</span>
              </div>
              <div className='bx-info-item'>
                <span className='bx-info-label'>Created At</span>
                <span className='bx-info-value'>{TimeConverter(facility.createdAt)}</span>
              </div>
              <div className='bx-info-item'>
                <span className='bx-info-label'>Last Updated</span>
                <span className='bx-info-value'>{TimeConverter(facility.updatedAt)}</span>
              </div>
              {facility.lastLimitReviewedAt && (
                <div className='bx-info-item'>
                  <span className='bx-info-label'>Last Limit Review</span>
                  <span className='bx-info-value'>{TimeConverter(facility.lastLimitReviewedAt)}</span>
                </div>
              )}
              {facility.cancellationPenaltyUntil && (
                <div className='bx-info-item'>
                  <span className='bx-info-label'>Penalty Until</span>
                  <span className='bx-info-value'>{TimeConverter(facility.cancellationPenaltyUntil)}</span>
                </div>
              )}
              {facility.isCancellationPenaltyActive !== undefined && (
                <div className='bx-info-item'>
                  <span className='bx-info-label'>Penalty Active</span>
                  <span className='bx-info-value'>
                    {facility.isCancellationPenaltyActive ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
            </div>
          </section>

          <section className='bx-facility-table-section'>
            <div className='bx-section-header'>
              <h2 className='bx-section-title'>Direct Debit Attempts</h2>
              <div className='bx-filters'>
                <select
                  className='bx-filter-select'
                  value={debitStatusFilter}
                  onChange={(e) => setDebitStatusFilter(e.target.value)}
                >
                  <option value=''>All Statuses</option>
                  <option value='pending'>Pending</option>
                  <option value='success'>Success</option>
                  <option value='failed'>Failed</option>
                  <option value='retrying'>Retrying</option>
                </select>
              </div>
            </div>
            <div className='bx-table-container'>
              {debitAttemptsLoading ? (
                <div className='bx-table-loading'>
                  <HorizontalLoader />
                </div>
              ) : debitAttemptsError ? (
                <div className='alert alert-danger'>{debitAttemptsError}</div>
              ) : debitAttempts.length === 0 ? (
                <div className='bx-empty-state'>No debit attempts found</div>
              ) : (
                <table className='bx-data-table'>
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Requested</th>
                      <th>Collected</th>
                      <th>Status</th>
                      <th>Reference</th>
                      <th>Initiator</th>
                      <th>Response</th>
                      <th>Created At</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debitAttempts.map((attempt, index) => (
                      <tr key={attempt.resourceId}>
                        <td>{index + 1 + (debitAttemptsPage - 1) * 20}</td>
                        <td className='bx-amount-cell'>₦{CurrencyFormatter(attempt.requestedAmountMajor)}</td>
                        <td className='bx-amount-cell'>₦{CurrencyFormatter(attempt.collectedAmountMajor)}</td>
                        <td>
                          <span
                            className='bx-status-badge'
                            style={{
                              backgroundColor: getDebitStatusBgColor(attempt.status),
                              color: getDebitStatusBadgeColor(attempt.status),
                            }}
                          >
                            {attempt.status.toUpperCase()}
                          </span>
                        </td>
                        <td className='bx-id-cell'>{attempt.reference || '-'}</td>
                        <td>{attempt.initiator}</td>
                        <td className='bx-response-cell'>
                          {attempt.responseMessage || '-'}
                        </td>
                        <td>{TimeConverter(attempt.createdAt)}</td>
                        <td>
                          <button
                            className='bx-action-btn bx-action-btn--reconcile'
                            onClick={() => handleReconcileAttempt(attempt.resourceId)}
                            disabled={reconcilingAttemptId === attempt.resourceId}
                          >
                            {reconcilingAttemptId === attempt.resourceId ? 'Reconciling...' : 'Reconcile'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <section className='bx-facility-table-section'>
            <div className='bx-section-header'>
              <h2 className='bx-section-title'>Repayment History</h2>
              <div className='bx-filters'>
                <select
                  className='bx-filter-select'
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                >
                  <option value=''>All Sources</option>
                  <option value='wallet_funding'>Wallet Funding</option>
                  <option value='direct_debit'>Direct Debit</option>
                </select>
              </div>
            </div>
            <div className='bx-table-container'>
              {repaymentsLoading ? (
                <div className='bx-table-loading'>
                  <HorizontalLoader />
                </div>
              ) : repaymentsError ? (
                <div className='alert alert-danger'>{repaymentsError}</div>
              ) : repayments.length === 0 ? (
                <div className='bx-empty-state'>No repayments found</div>
              ) : (
                <table className='bx-data-table'>
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Amount</th>
                      <th>Source</th>
                      <th>Reference</th>
                      <th>Drawdown ID</th>
                      <th>Order ID</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {repayments.map((repayment, index) => (
                      <tr key={repayment.resourceId}>
                        <td>{index + 1 + (repaymentsPage - 1) * 20}</td>
                        <td className='bx-amount-cell'>₦{CurrencyFormatter(repayment.amountMajor)}</td>
                        <td>
                          <span
                            className='bx-source-badge'
                            style={{
                              backgroundColor: repayment.source === 'wallet_funding' ? '#dbeafe' : '#d1fae5',
                              color: repayment.source === 'wallet_funding' ? '#3b82f6' : '#10b981',
                            }}
                          >
                            {repayment.source.replace(/_/g, ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className='bx-id-cell'>{repayment.sourceReference || '-'}</td>
                        <td className='bx-id-cell'>{repayment.drawdownResourceId || '-'}</td>
                        <td className='bx-id-cell'>{repayment.orderResourceId || '-'}</td>
                        <td>{TimeConverter(repayment.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>
      )}

      {/* Direct Debit Modal */}
      {showDebitModal && (
        <div className='bx-debit-modal-overlay' onClick={() => setShowDebitModal(false)}>
          <div className='bx-debit-modal' onClick={(e) => e.stopPropagation()}>
            <div className='bx-debit-modal-header'>
              <div className='bx-debit-modal-icon'>
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 13H11V7H13V13ZM13 17H11V15H13V17Z' fill='#ec4899'/>
                </svg>
              </div>
              <div className='bx-debit-modal-title-section'>
                <h3 className='bx-debit-modal-title'>Trigger Direct Debit</h3>
                <p className='bx-debit-modal-subtitle'>Enter the amount to debit from the user&apos;s account</p>
              </div>
              <button className='bx-debit-modal-close' onClick={() => setShowDebitModal(false)}>
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M18 6L6 18M6 6L18 18' stroke='#6b7280' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                </svg>
              </button>
            </div>

            <form className='bx-debit-modal-form' onSubmit={handleDebitSubmit}>
              <div className='bx-debit-modal-balance-info'>
                <span className='bx-debit-modal-balance-label'>Outstanding Balance</span>
                <span className='bx-debit-modal-balance-value'>₦{CurrencyFormatter(facility?.outstandingMajor ?? 0)}</span>
              </div>

              <div className='bx-debit-modal-field'>
                <label className='bx-debit-modal-label'>
                  Debit Amount <span className='bx-debit-modal-required'>*</span>
                </label>
                <div className='bx-debit-modal-input-wrapper'>
                  <span className='bx-debit-modal-currency'>₦</span>
                  <input
                    type='number'
                    className='bx-debit-modal-input'
                    value={debitAmount}
                    onChange={(e) => setDebitAmount(e.target.value)}
                    placeholder='Enter amount'
                    min='0'
                    step='0.01'
                    required
                  />
                </div>
                <small className='bx-debit-modal-hint'>Prefilled with outstanding balance. You can edit this amount.</small>
              </div>

              <div className='bx-debit-modal-actions'>
                <button
                  type='button'
                  className='bx-debit-modal-btn bx-debit-modal-btn--cancel'
                  onClick={() => setShowDebitModal(false)}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='bx-debit-modal-btn bx-debit-modal-btn--submit'
                  disabled={debitSubmitting}
                >
                  {debitSubmitting ? 'Processing...' : 'Trigger Debit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bank Balance Amount Input Modal */}
      {showBalanceAmountModal && (
        <div className='bx-balance-amount-modal-overlay' onClick={() => setShowBalanceAmountModal(false)}>
          <div className='bx-balance-amount-modal' onClick={(e) => e.stopPropagation()}>
            <div className='bx-balance-amount-modal-header'>
              <div className='bx-balance-amount-modal-icon'>
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 6C12.55 6 13 6.45 13 7V11C13 11.55 12.55 12 12 12C11.45 12 11 11.55 11 11V7C11 6.45 11.45 6 12 6ZM12 14C12.55 14 13 14.45 13 15C13 15.55 12.55 16 12 16C11.45 16 11 15.55 11 15C11 14.45 11.45 14 12 14Z' fill='#E873A8'/>
                </svg>
              </div>
              <div className='bx-balance-amount-modal-title-section'>
                <h3 className='bx-balance-amount-modal-title'>Check Bank Balance</h3>
                <p className='bx-balance-amount-modal-subtitle'>Enter the amount to check sufficiency for</p>
              </div>
              <button className='bx-balance-amount-modal-close' onClick={() => setShowBalanceAmountModal(false)}>
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M18 6L6 18M6 6L18 18' stroke='#6b7280' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                </svg>
              </button>
            </div>

            <form className='bx-balance-amount-modal-form' onSubmit={(e) => { e.preventDefault(); loadBankBalance(); }}>
              <div className='bx-balance-amount-modal-balance-info'>
                <span className='bx-balance-amount-modal-balance-label'>Outstanding Balance</span>
                <span className='bx-balance-amount-modal-balance-value'>₦{CurrencyFormatter(facility?.outstandingMajor ?? 0)}</span>
              </div>

              <div className='bx-balance-amount-modal-field'>
                <label className='bx-balance-amount-modal-label'>
                  Check Amount <span className='bx-balance-amount-modal-required'>*</span>
                </label>
                <div className='bx-balance-amount-modal-input-wrapper'>
                  <span className='bx-balance-amount-modal-currency'>₦</span>
                  <input
                    type='number'
                    className='bx-balance-amount-modal-input'
                    value={balanceAmount}
                    onChange={(e) => setBalanceAmount(e.target.value)}
                    placeholder='Enter amount'
                    min='0'
                    step='0.01'
                    required
                  />
                </div>
                <small className='bx-balance-amount-modal-hint'>Prefilled with outstanding balance. You can edit this amount.</small>
              </div>

              <div className='bx-balance-amount-modal-actions'>
                <button
                  type='button'
                  className='bx-balance-amount-modal-btn bx-balance-amount-modal-btn--cancel'
                  onClick={() => setShowBalanceAmountModal(false)}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='bx-balance-amount-modal-btn bx-balance-amount-modal-btn--submit'
                >
                  Check Balance
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bank Balance Check Modal */}
      {showBalanceModal && (
        <div className='bx-balance-modal-overlay' onClick={() => setShowBalanceModal(false)}>
          <div className='bx-balance-modal' onClick={(e) => e.stopPropagation()}>
            <div className='bx-balance-modal-header'>
              <div className='bx-balance-modal-icon'>
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 6C12.55 6 13 6.45 13 7V11C13 11.55 12.55 12 12 12C11.45 12 11 11.55 11 11V7C11 6.45 11.45 6 12 6ZM12 14C12.55 14 13 14.45 13 15C13 15.55 12.55 16 12 16C11.45 16 11 15.55 11 15C11 14.45 11.45 14 12 14Z' fill='#E873A8'/>
                </svg>
              </div>
              <div className='bx-balance-modal-title-section'>
                <h3 className='bx-balance-modal-title'>Bank Account Balance</h3>
                <p className='bx-balance-modal-subtitle'>Live bank account balance and sufficiency check</p>
              </div>
              <button className='bx-balance-modal-close' onClick={() => setShowBalanceModal(false)}>
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M18 6L6 18M6 6L18 18' stroke='#6b7280' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                </svg>
              </button>
            </div>

            <div className='bx-balance-modal-body'>
              {balanceLoading ? (
                <div className='bx-balance-modal-loading'>
                  <HorizontalLoader />
                </div>
              ) : balanceError ? (
                <div className='alert alert-danger'>{balanceError}</div>
              ) : balanceData ? (
                <>
                  <div className='bx-balance-metrics'>
                    <div className='bx-balance-metric-card'>
                      <span className='bx-balance-metric-label'>Account Balance</span>
                      <span className='bx-balance-metric-value'>₦{CurrencyFormatter(balanceData.accountBalanceMajor ?? 0)}</span>
                    </div>
                    <div className='bx-balance-metric-card'>
                      <span className='bx-balance-metric-label'>Total Due</span>
                      <span className='bx-balance-metric-value'>₦{CurrencyFormatter(balanceData.totalDueMajor ?? 0)}</span>
                    </div>
                    <div className='bx-balance-metric-card'>
                      <span className='bx-balance-metric-label'>Requested Amount</span>
                      <span className='bx-balance-metric-value'>₦{CurrencyFormatter(balanceData.requestedAmountMajor ?? 0)}</span>
                    </div>
                  </div>

                  <div className={`bx-balance-sufficiency ${balanceData.hasSufficientBalance ? 'bx-balance-sufficiency--success' : 'bx-balance-sufficiency--warning'}`}>
                    <div className='bx-balance-sufficiency-icon'>
                      {balanceData.hasSufficientBalance ? (
                        <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                          <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z' fill='#10b981'/>
                        </svg>
                      ) : (
                        <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                          <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM11 15H13V17H11V15ZM11 7H13V13H11V7Z' fill='#f59e0b'/>
                        </svg>
                      )}
                    </div>
                    <div className='bx-balance-sufficiency-content'>
                      <span className='bx-balance-sufficiency-title'>
                        {balanceData.hasSufficientBalance ? 'Sufficient Balance' : 'Insufficient Balance'}
                      </span>
                      <span className='bx-balance-sufficiency-text'>
                        {balanceData.hasSufficientBalance
                          ? 'The user\'s bank account has sufficient funds to cover the due amount.'
                          : 'The user\'s bank account does not have sufficient funds to cover the due amount.'}
                      </span>
                    </div>
                  </div>

                  {balanceData.hasSufficientBalance && (
                    <div className='bx-balance-modal-actions'>
                      <button
                        className='bx-balance-modal-btn bx-balance-modal-btn--cancel'
                        onClick={() => setShowBalanceModal(false)}
                      >
                        Close
                      </button>
                      <button
                        className='bx-balance-modal-btn bx-balance-modal-btn--trigger'
                        onClick={async () => {
                          if (!facilityId) return
                          try {
                            setDebitSubmitting(true)
                            const response = await boostCreditBXService.triggerDirectDebit(facilityId, balanceData.totalDueMajor ?? 0)
                            if (response) {
                              handleSuccess('Direct debit triggered successfully')
                              setShowBalanceModal(false)
                              loadDebitAttempts()
                            } else {
                              handleError({ message: 'Failed to trigger direct debit' })
                            }
                          } catch (e: any) {
                            handleError(e)
                          } finally {
                            setDebitSubmitting(false)
                          }
                        }}
                        disabled={debitSubmitting}
                      >
                        {debitSubmitting ? 'Processing...' : 'Trigger Direct Debit'}
                      </button>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      <ToastContainer></ToastContainer>
    </div>
  )
}

export default BoostCreditFacilityDetails
